import { ChangeEvent, FC, forwardRef, useEffect, useState } from 'react';
import {
  labelStyles,
  StyledCharCounter,
  StyledContainer,
  StyledTextArea,
  StyledTextContainer,
  StyledSecondaryLabel,
} from './TextArea.styles';
import { useTranslation } from 'react-i18next';
import { COMMON_TRANSLATION } from '@/common/constants';
import { TextAreaProps } from './TextArea.types';
import Label from '../Label/Label';
import { Variants } from '@common/types';

const TEXT_AREA_MAX_LENGHT = 500;

const TextArea: FC<TextAreaProps> = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      css,
      form,
      id,
      label,
      secondaryLabel,
      maxLength = TEXT_AREA_MAX_LENGHT,
      name,
      onBlur,
      onChange,
      placeholder,
      defaultValue,
      role,
      testId = 'textarea-test-id',
      type = 'text',
      value,
      variant,
      isSensitive = false,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation(COMMON_TRANSLATION);
    const [remainingCharacters, setRemainingCharacters] = useState(maxLength);
    const hasDefaultValue = typeof defaultValue !== 'undefined';
    const [textAreaValue, setTextAreaValue] = useState<string | number | undefined>(
      hasDefaultValue ? defaultValue : '',
    );

    useEffect(() => {
      setTextAreaValue(value ?? '');
    }, [value]);

    const handleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
      const { value, maxLength } = event.target;
      if (maxLength) {
        setTextAreaValue(value.slice(0, maxLength));
        setRemainingCharacters(maxLength - value.length);
      }
      onChange?.(event);
    };

    const textAreaProps = {
      form,
      id,
      name,
      onBlur,
      placeholder,
      role,
      css,
      maxLength,
      type,
    };

    return (
      <StyledContainer>
        <StyledTextContainer>
          {label && <Label css={labelStyles}>{label}</Label>}
          {secondaryLabel && variant && variant === Variants.PRIMARY && (
            <StyledSecondaryLabel htmlFor={id}>
              <em>{secondaryLabel}</em>
            </StyledSecondaryLabel>
          )}
        </StyledTextContainer>
        <StyledTextArea
          {...textAreaProps}
          {...rest}
          value={textAreaValue}
          data-testid={testId}
          onChange={handleTextAreaChange}
          ref={ref}
          className={isSensitive ? 'rb-mask' : ''}
        />
        <StyledCharCounter>{`${remainingCharacters} ${t('characters_remaining')}`}</StyledCharCounter>
      </StyledContainer>
    );
  },
);

TextArea.displayName = 'TextArea';

export default TextArea;
